import type { Options } from 'ky';
import ky, { HTTPError } from 'ky';
import qs from 'qs';
import { nanoid } from 'nanoid';
import { XSRFHelper } from '../../legacy/lib/util/xsrf-helper';
import { normalizeQueryParams } from './normalize-query-params';

type CreateOptions = Omit<Options, 'window' | 'method' | 'fetch'>;

// Оставил то, что посчитал нужным, можно будет докинуть потом при желании дополнительных параметров
type FetchOptions = Pick<Options, 'hooks' | 'headers' | 'timeout' | 'body' | 'json'> & {
  searchParams?: Record<string, any> | Options['searchParams'];
};

function stringifySearchParams(params: unknown) {
  return qs.stringify(normalizeQueryParams(params), {
    arrayFormat: 'repeat',
    encodeValuesOnly: true
  });
}

function createHttpClient(options: CreateOptions = {}) {
  const httpClient = ky.create({
    retry: 0,
    throwHttpErrors: true,
    ...options,
    headers: {
      Accept: 'application/json',
      'X-XSRFToken': XSRFHelper.getToken(),
      ...options.headers
    }
  });

  httpClient.extend({
    hooks: {
      beforeRequest: [
        (request) => {
          request.headers.set('X-Request-Id', nanoid());
        }
      ]
    }
  });

  return {
    get: <R>(url: string, { searchParams, ...options }: FetchOptions = {}) => httpClient.get<R>(url, {
      searchParams: stringifySearchParams(searchParams),
      ...options
    }),
    post: <R>(url: string, { searchParams, ...options }: FetchOptions = {}) => httpClient.post<R>(url, {
      searchParams: stringifySearchParams(searchParams),
      ...options
    }),
    put: <R>(url: string, { searchParams, ...options }: FetchOptions = {}) => httpClient.put<R>(url, {
      searchParams: stringifySearchParams(searchParams),
      ...options
    }),
    delete: <R>(url: string, { searchParams, ...options }: FetchOptions = {}) => httpClient.delete<R>(url, {
      searchParams: stringifySearchParams(searchParams),
      ...options
    }),
    patch: <R>(url: string, { searchParams, ...options }: FetchOptions = {}) => httpClient.patch<R>(url, {
      searchParams: stringifySearchParams(searchParams),
      ...options
    }),
    head: (url: string, { searchParams, ...options }: FetchOptions = {}) => httpClient.head(url, {
      searchParams: stringifySearchParams(searchParams),
      ...options
    })
  };
}

function isHttpError<E = unknown>(error: unknown): error is HTTPError<E> {
  return error instanceof HTTPError;
}

function assertHttpError<E = unknown>(error: unknown): asserts error is HTTPError<E> {
  if (!isHttpError(error)) {
    throw error;
  }
}

export const httpClient = {
  create: createHttpClient,
  defaultClient: createHttpClient(),
  defaultStringifySearchParams: stringifySearchParams,
  isHttpError,
  assertHttpError
};
